package org.example.project.yard

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.dashboard.ContentOffset
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.engineservice.ServiceApi
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File

@Composable
fun YardLieferscheinUploadPopup(
    entry: All,
    onClose: () -> Unit,
    onUploaded: suspend (All) -> Boolean
) {
    val scope = rememberCoroutineScope()
    var selectedFile by remember(entry.id, entry.lieferscheinnummer) { mutableStateOf<File?>(null) }
    var loading by remember(entry.id, entry.lieferscheinnummer) { mutableStateOf(false) }
    var info by remember(entry.id, entry.lieferscheinnummer) { mutableStateOf("") }
    var fileInput by remember(entry.id, entry.lieferscheinnummer) { mutableStateOf<HTMLInputElement?>(null) }
    val entryId = yardLieferscheinEntryId(entry)
    val serviceType = yardLieferscheinServiceType(entry)

    PopupEngineCard(
        windowKey = "yard_lieferschein_upload_${yardLieferscheinRowKey(entry)}",
        widthPx = 620,
        heightPx = 330,
        minWidthPx = 560,
        minHeightPx = 300,
        initialX = 360.0,
        initialY = 170.0,
        contentInsetLeft = ContentOffset.LEFT.value.toDouble(),
        contentInsetTop = ContentOffset.TOP.value.toDouble(),
        contentInsetRight = ContentOffset.RIGHT.value.toDouble(),
        contentInsetBottom = ContentOffset.BOTTOM.value.toDouble(),
        onClose = onClose
    ) {
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(18.px)
                padding(24.px)
                color(DashboardTheme.TEXT_LIGHT)
            }
        }) {
            H3({
                style {
                    margin(0.px)
                    fontSize(20.px)
                    fontWeight("700")
                }
            }) { Text("Lieferschein hinzufügen") }

            Span({
                style {
                    fontSize(15.px)
                    lineHeight("1.45")
                    color(DashboardTheme.TEXT_MUTED)
                }
            }) {
                Text("Bitte wählen Sie ein PDF aus und fügen Sie es dem Auftrag hinzu.")
            }

            Input(InputType.File, attrs = {
                accept("application/pdf,.pdf")
                style { display(DisplayStyle.None) }
                ref { element ->
                    fileInput = element
                    onDispose { fileInput = null }
                }
                onInput { event ->
                    val input = event.target as? HTMLInputElement
                    selectedFile = input?.files?.item(0)
                    info = selectedFile?.name.orEmpty()
                }
            })

            LumaticaButton(
                label = "PDF importieren",
                onClick = { if (!loading) fileInput?.click() },
                enabled = !loading
            )

            if (info.isNotBlank()) {
                Span({
                    style {
                        fontSize(13.px)
                        color(DashboardTheme.TEXT_MUTED)
                    }
                }) { Text(info) }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                    marginTop(8.px)
                }
            }) {
                LumaticaButton(
                    label = "Abbrechen",
                    onClick = { if (!loading) onClose() },
                    enabled = !loading
                )
                LumaticaButton(
                    label = if (loading) "..." else "Hinzufügen",
                    onClick = {
                        val pdf = selectedFile ?: run {
                            info = "Bitte zuerst eine PDF auswählen."
                            return@LumaticaButton
                        }
                        if (loading || entryId.isBlank() || serviceType.isBlank()) return@LumaticaButton
                        loading = true
                        info = "Lieferschein wird hinzugefügt..."
                        scope.launch {
                            val uploadedUrl = ServiceApi.uploadServiceDocument(
                                type = serviceType,
                                entryId = entryId,
                                file = pdf,
                                fileName = yardLieferscheinFileName(entry)
                            )
                            val found = if (uploadedUrl.isNotBlank()) onUploaded(entry) else false
                            loading = false
                            if (found) {
                                AppDialogs.alert("Lieferschein erfolgreich hinzugefügt.")
                                onClose()
                            } else {
                                info = "Lieferschein wurde im Auftrag-Dokument nicht gefunden."
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Action,
                    enabled = !loading && selectedFile != null && entryId.isNotBlank() && serviceType.isNotBlank()
                )
            }
        }
    }
}

fun yardLieferscheinServiceType(entry: All): String =
    when (yardLieferscheinAuftragsTyp(entry)) {
        "WA" -> "yard-wa"
        else -> ""
    }

fun yardLieferscheinEntryId(entry: All): String =
    if (yardLieferscheinAuftragsTyp(entry) == "WA") {
        listOf(
            entry.lieferscheinnummer.trim(),
            entry.id.trim(),
            entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty()
        ).joinToString("|")
    } else {
        ""
    }

fun yardLieferscheinFileName(entry: All): String {
    val ls = entry.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = entry.lfdNr.takeIf { it > 0 }?.toString()
        ?: entry.id.ifBlank { "1" }
    return "${ls}-${lkw}_lieferschein.pdf"
}

fun yardLieferscheinRowKey(entry: All): String =
    "${yardLieferscheinAuftragsTyp(entry)}:${entry.id}:${entry.lfdNr}:${entry.lieferscheinnummer}"

private fun yardLieferscheinAuftragsTyp(entry: All): String =
    entry.auftragsart.trim().uppercase()
        .ifBlank { entry.slotBereich.trim().uppercase() }
        .takeIf { it == "WE" || it == "WA" }
        ?: ""
